import React from 'react';
import { SystemShared } from '../../../../shared/system';
import { UserDataWeb, UserOrdersWeb } from '../../../../shared/userdataweb';
import { LangBlock } from '../../global';
import { AdminDataTable } from './admin.table';
import { AdminPagination } from './admin.pagination';
import { usePaginatedTable } from './usePaginatedTable';
import { AdminPageWrap, AdminSection, AdminBadge } from './admin.ui';
import { AdminAvatar, AdminExternalLink, AdminUserActions } from './admin.user-actions';

type Props = {
    usersList: UserDataWeb[];
    ordersList: UserOrdersWeb[];
    loadUsersList: () => void;
};

const socialUrl = (type: string, value: string) => {
    if (type === 'twitch') {
        return `https://twitch.tv/${value}`;
    }

    if (type === 'trovo') {
        return `https://trovo.live/${value}`;
    }

    if (type === 'telegram_name') {
        return `https://t.me/${value}`;
    }

    return '#';
};

export const AdminUsers = ({ usersList, ordersList, loadUsersList }: Props) => {
    const [, , LangString] = LangBlock();
    const table = usePaginatedTable(usersList, 50);

    return (
        <AdminPageWrap>
            <AdminSection
                title={LangString('AdminUsersTitle', usersList.length)}
                subtitle={LangString('AdminUsersSubtitle')}
            >
                <AdminDataTable
                    data={table.pageData}
                    sortColumn={table.sortColumn}
                    sortType={table.sortType}
                    onSort={table.handleSort}
                    rowKey={(row) => row.id}
                    emptyMessage={LangString('AdminNoUsers')}
                    columns={[
                        {
                            key: 'id',
                            label: LangString('AdminId'),
                            sortable: true,
                            primary: true,
                            render: (row) => `#${row.id}`,
                        },
                        {
                            key: 'avatar',
                            label: LangString('AdminAvatar'),
                            hideMobile: true,
                            render: (row) => <AdminAvatar src={row.avatar} alt={row.name} />,
                        },
                        {
                            key: 'name',
                            label: LangString('AdminUsername'),
                            sortable: true,
                            render: (row) => row.name,
                        },
                        {
                            key: 'balance',
                            label: LangString('AdminBalance'),
                            render: (row) => `$${SystemShared.numberFormat(row.balance)}`,
                        },
                        {
                            key: 'spent',
                            label: LangString('AdminSpent'),
                            render: (row) => `$${SystemShared.numberFormat(row.spent || 0)}`,
                        },
                        {
                            key: 'twitch',
                            label: 'Twitch',
                            hideMobile: true,
                            render: (row) =>
                                row.twitch ? (
                                    <AdminExternalLink href={socialUrl('twitch', row.twitch)}>
                                        {row.twitch}
                                    </AdminExternalLink>
                                ) : (
                                    '—'
                                ),
                        },
                        {
                            key: 'trovo',
                            label: 'Trovo',
                            hideMobile: true,
                            render: (row) =>
                                row.trovo ? (
                                    <AdminExternalLink href={socialUrl('trovo', row.trovo)}>
                                        {row.trovo}
                                    </AdminExternalLink>
                                ) : (
                                    '—'
                                ),
                        },
                        {
                            key: 'telegram_name',
                            label: 'Telegram',
                            hideMobile: true,
                            render: (row) =>
                                row.telegram_name ? (
                                    <AdminExternalLink
                                        href={socialUrl('telegram_name', row.telegram_name)}
                                    >
                                        {row.telegram_name}
                                    </AdminExternalLink>
                                ) : (
                                    '—'
                                ),
                        },
                        {
                            key: 'discord_name',
                            label: 'Discord',
                            hideMobile: true,
                            render: (row) => row.discord_name || '—',
                        },
                        {
                            key: 'created',
                            label: LangString('AdminCreated'),
                            hideMobile: true,
                            render: (row) =>
                                row.created
                                    ? SystemShared.timeStampString(row.created)
                                    : LangString('AdminNoDate'),
                        },
                        {
                            key: 'lastUpdate',
                            label: LangString('AdminUpdated'),
                            hideMobile: true,
                            render: (row) =>
                                row.lastUpdate
                                    ? SystemShared.timeStampString(row.lastUpdate)
                                    : LangString('AdminNoDate'),
                        },
                        {
                            key: 'lang',
                            label: LangString('AdminLang'),
                            render: (row) => row.lang,
                        },
                        {
                            key: 'test',
                            label: LangString('AdminTest'),
                            render: (row) => (
                                <AdminBadge tone={row.test ? 'ok' : 'neutral'}>
                                    {row.test ? LangString('AdminYes') : LangString('AdminNo')}
                                </AdminBadge>
                            ),
                        },
                        {
                            key: 'excludeFromStats',
                            label: LangString('AdminExcludeFromStats'),
                            render: (row) => (
                                <AdminBadge tone={row.excludeFromStats ? 'warn' : 'neutral'}>
                                    {row.excludeFromStats
                                        ? LangString('AdminYes')
                                        : LangString('AdminNo')}
                                </AdminBadge>
                            ),
                        },
                        {
                            key: 'creatorBanned',
                            label: LangString('AdminCreatorBan'),
                            render: (row) => (
                                <AdminBadge tone={row.creatorBanned ? 'danger' : 'neutral'}>
                                    {row.creatorBanned ? LangString('AdminYes') : LangString('AdminNo')}
                                </AdminBadge>
                            ),
                        },
                        {
                            key: 'recommendedDeveloper',
                            label: LangString('AdminRecommendedDev'),
                            render: (row) => (
                                <AdminBadge tone={row.recommendedDeveloper ? 'ok' : 'neutral'}>
                                    {row.recommendedDeveloper ? LangString('AdminYes') : LangString('AdminNo')}
                                </AdminBadge>
                            ),
                        },
                        {
                            key: 'ref',
                            label: LangString('AdminReferral'),
                            hideMobile: true,
                            render: (row) => {
                                const rec = usersList.find((q) => q.id === row.ref);

                                return rec ? `${rec.name} (${rec.id})` : `${row.ref}`;
                            },
                        },
                        {
                            key: 'referrals',
                            label: LangString('AdminReferrals'),
                            hideMobile: true,
                            render: (row) => SystemShared.numberFormat(row.referrals),
                        },
                        {
                            key: 'welcomedone',
                            label: LangString('AdminWelcomeBonus'),
                            hideMobile: true,
                            render: (row) => (row.welcomedone ? LangString('AdminWelcomeUsed') : '—'),
                        },
                        {
                            key: 'orders',
                            label: LangString('AdminActiveOrders'),
                            render: (row) =>
                                ordersList.filter((q) => q.user === row.id).length,
                        },
                        {
                            key: 'actions',
                            label: LangString('AdminActions'),
                            render: (row) => (
                                <AdminUserActions
                                    userId={row.id}
                                    creatorBanned={row.creatorBanned}
                                    recommendedDeveloper={row.recommendedDeveloper}
                                    onDone={loadUsersList}
                                />
                            ),
                        },
                    ]}
                />
                <AdminPagination
                    page={table.page}
                    totalPages={table.totalPages}
                    total={table.total}
                    limit={table.limit}
                    limitOptions={[10, 30, 50, 100, 500, 1000, 10000]}
                    onPageChange={table.setPage}
                    onLimitChange={table.setLimit}
                />
            </AdminSection>
        </AdminPageWrap>
    );
};
